import { css } from '@emotion/react'
import {
  Alert,
  Heading,
  HFlow,
  InfoLabel,
  isEmpty,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
  VFlow,
} from 'bold-ui'
import { booleanToSimNaoEnum, simNaoEnumRecord } from 'components/form'
import { TableBox } from 'components/table'
import { calcularIMC } from 'util/atendimento'
import { formatNumber } from 'util/number'
import { stringGrouper } from 'util/string/string-grouper'
import { IdadeOrigemAtendimento } from 'view/atendimentos/detail/historico/model/model-historico'
import { mountInfosAdministrativasAtividadeColetiva } from 'view/atendimentos/detail/historico/utils/utils-historico'
import {
  publicoAlvoRecord,
  temaSaudeRecord,
  tipoAtividadeRecord,
} from 'view/atividade-coletiva/model-atividadeColetiva'
import { getPraticasSaudeLabels } from 'view/atividade-coletiva/saude/utils-atividadeColetivaSaude'

import HistoricoGapPanel from '../../HistoricoGapPanel'
import { HistoricoAtividadeColetivaSaudeModel } from '../model-historicoAtividadeColetiva'

interface HistoricoAtividadeColetivaPanelSaudeProps {
  readonly atividadeColetiva: HistoricoAtividadeColetivaSaudeModel
  readonly idadeOrigemAtendimento: IdadeOrigemAtendimento
  readonly isFonteDadosPec: boolean
}

export default function HistoricoAtividadeColetivaPanelSaude({
  atividadeColetiva,
  idadeOrigemAtendimento,
  isFonteDadosPec,
}: HistoricoAtividadeColetivaPanelSaudeProps) {
  return (
    <HistoricoGapPanel
      idadeOrigemAtendimento={idadeOrigemAtendimento}
      informacoesAdministrativas={mountInfosAdministrativasAtividadeColetiva(atividadeColetiva)}
    >
      <VFlow vSpacing={0.5}>
        {atividadeColetiva.atividadeEducacaoPse && !atividadeColetiva.atividadeSaudePse && (
          <Alert type='info' inline>
            <Text color='primary' fontWeight='bold'>
              Atividade não acompanhada por um profissional de saúde
            </Text>
          </Alert>
        )}
        {atividadeColetiva.participante?.avaliacaoAlterada && (
          <div css={styles.avaliacaoAlterada}>
            <Alert type='info' inline>
              <Text color='primary' fontWeight='bold'>
                Avaliação alterada
              </Text>
            </Alert>
          </div>
        )}
        {(!!atividadeColetiva.participante?.peso || !!atividadeColetiva.participante?.altura) && (
          <TableBox css={styles.tableBoxAntropometria}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>
                    <Text fontWeight='bold'>Antropometria</Text>
                  </TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell style={styles.tableCellAntropometria}>
                    <HFlow hSpacing={6}>
                      {!!atividadeColetiva.participante?.peso && (
                        <InfoLabel title='Peso'>{`${atividadeColetiva.participante.peso} kg`}</InfoLabel>
                      )}
                      {!!atividadeColetiva.participante?.altura && (
                        <InfoLabel title='Altura'>{`${atividadeColetiva.participante.altura} cm`}</InfoLabel>
                      )}
                      {!!atividadeColetiva.participante?.peso && !!atividadeColetiva.participante?.altura && (
                        <InfoLabel title='IMC'>{`${formatNumber(
                          calcularIMC(atividadeColetiva.participante.peso, atividadeColetiva.participante.altura)
                        )} kg/m²`}</InfoLabel>
                      )}
                    </HFlow>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableBox>
        )}
        <Heading level={3}>Atividade</Heading>
        <Text>{tipoAtividadeRecord[atividadeColetiva.tipoAtividade]}</Text>
        {!isEmpty(atividadeColetiva.publicoAlvo) && (
          <VFlow vSpacing={0.1}>
            <Heading level={5}>Público-alvo</Heading>
            <Text>
              {stringGrouper(
                ...atividadeColetiva.publicoAlvo
                  .map((alvo) => publicoAlvoRecord[alvo])
                  .sort((a, b) => a.localeCompare(b))
              )}
            </Text>
          </VFlow>
        )}
        {!isEmpty(atividadeColetiva.temasSaude) && (
          <VFlow vSpacing={0.1}>
            <Heading level={5}>Temas para saúde</Heading>
            <Text>
              {stringGrouper(
                ...atividadeColetiva.temasSaude.map((tema) => temaSaudeRecord[tema]).sort((a, b) => a.localeCompare(b))
              )}
            </Text>
          </VFlow>
        )}
        {!isEmpty(atividadeColetiva.praticasSaude) && (
          <VFlow vSpacing={0.1}>
            <Heading level={5}>Prática em saúde</Heading>
            <Text>{stringGrouper(...getPraticasSaudeLabels(atividadeColetiva.praticasSaude))}</Text>
          </VFlow>
        )}
        {atividadeColetiva.procedimento && (
          <TableBox css={styles.tableBoxProcedimento}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>
                    <Text fontWeight='bold'>Outro procedimento coletivo</Text>
                  </TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <HFlow hSpacing={0.5} style={styles.HFlowProcedimento}>
                    <Text>
                      {`${atividadeColetiva.procedimento.descricao} - ${atividadeColetiva.procedimento.codigo}`}
                    </Text>
                  </HFlow>
                </TableRow>
              </TableBody>
            </Table>
          </TableBox>
        )}
        {isFonteDadosPec && (
          <VFlow vSpacing={0.1}>
            <Heading level={5}>Programa Nacional de Controle de Tabagismo</Heading>
            <Text>
              {stringGrouper(
                `Abandonou o grupo: ${
                  simNaoEnumRecord[booleanToSimNaoEnum(atividadeColetiva.participante.pnctAbandonouGrupo)] ??
                  'Não informado'
                }`,
                `Cessou o hábito de fumar: ${
                  simNaoEnumRecord[booleanToSimNaoEnum(atividadeColetiva.participante.pnctCessouHabitoFumar)] ??
                  'Não informado'
                }`
              )}
            </Text>
          </VFlow>
        )}
        {!isFonteDadosPec &&
          (atividadeColetiva.participante.pnctAbandonouGrupo ||
            atividadeColetiva.participante.pnctCessouHabitoFumar) && (
            <VFlow vSpacing={0.1}>
              <Heading level={5}>Programa Nacional de Controle de Tabagismo</Heading>
              <Text>
                {stringGrouper(
                  ...(atividadeColetiva.participante.pnctAbandonouGrupo ? ['Abandonou o grupo'] : []),
                  ...(atividadeColetiva.participante.pnctCessouHabitoFumar ? ['Cessou o hábito de fumar'] : [])
                )}
              </Text>
            </VFlow>
          )}
        {isFonteDadosPec && atividadeColetiva.anotacoes?.removeTags() && (
          <VFlow vSpacing={0.1}>
            <Heading level={5}>Anotações sobre atividade</Heading>
            <Text dangerouslySetInnerHTML={{ __html: atividadeColetiva.anotacoes }} style={styles.breakWord} />
          </VFlow>
        )}
      </VFlow>
    </HistoricoGapPanel>
  )
}

const styles = {
  avaliacaoAlterada: css`
    margin: 0.5rem 0;
  `,
  tableBoxProcedimento: css`
    margin-bottom: 0.5rem;
  `,
  HFlowProcedimento: css`
    padding: 0.5rem 1rem;
  `,
  tableCellAntropometria: css`
    padding: 0.8rem;
  `,
  tableBoxAntropometria: css`
    margin: 0.5rem 0;
    max-width: 30rem;
  `,
  breakWord: css`
    word-break: break-word;
  `,
}
